import DataTable from '@/components/features/data/DataTable'
import { groupColumns } from '@/components/features/data/DataTable/ColumnDefinitions/GroupColumns'
import EditNameModal from '@/components/features/data/EditNameModal'
import {
  CreateGroupForm,
  type CreateGroupFormValues,
} from '@/components/features/organisations/groups/CreateGroupForm'
import type { GroupMemberOption } from '@/components/features/organisations/groups/GroupMembersField'
import {
  type GroupSetupResult,
  createGroupErrorMessage,
  groupSetupHasFailures,
  runGroupSetup,
} from '@/components/features/organisations/groups/groupSetup'
import Modal from '@/components/layout/Modal'
import ConfirmationDialog from '@/components/utils/ConfirmationDialog'
import { useAuth } from '@/context/AuthContext'
import { useClient } from '@/context/ClientContext'
import { useOffsetPaginationQuery } from '@/hooks/useOffsetPaginationQuery'
import { useOrganisationUsersWithUserMap } from '@/hooks/useOrganisationUsersWithUserMap'
import { usePermissionCheck } from '@/hooks/usePermissionCheck'
import { fetchGroupsPaginated } from '@/lib/clientHelpers'
import { getUserFullName } from '@/lib/utils'
import { PlusCircleIcon } from '@heroicons/react/24/outline'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useMemo, useState } from 'react'

import {
  Group,
  UpdateGroupInput,
} from '@droidmap/organisation-service-contract'
import { Permission } from '@droidmap/permissions-service-contract'

const PREFERENCE_KEY = 'ORGANISATION_GROUPS'

export const Route = createFileRoute('/_authenticated/orgs/$orgId/groups/')({
  component: Groups,
  loader: () => ({ crumb: 'Groups' }),
})

function Groups() {
  const auth = useAuth()
  const { orgId } = Route.useParams()
  const { metadata } = Route.useRouteContext()
  const queryClient = useQueryClient()
  const [createItemOpen, setCreateItemOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [groupToDelete, setGroupToDelete] = useState<Group | null>(null)
  const [groupToEdit, setGroupToEdit] = useState<Group | null>(null)

  const { organisationClient, permissionsClient, userClient } = useClient()

  // The Create Group modal offers initial members and an org-level permission
  // (#3135) only to someone holding the permission each write needs. The
  // backend checks them again; this just keeps the modal from offering what
  // it would refuse.
  const { hasPermission } = usePermissionCheck()
  const canAddMembers = hasPermission(Permission.CREATE_GROUP_USER)
  const canGrant = hasPermission(Permission.CREATE_ORG_GRANT)

  const {
    orgUsers,
    userMap,
    isLoading: isLoadingMembers,
  } = useOrganisationUsersWithUserMap({
    orgId,
    organisationClient,
    userClient,
    metadata,
  })

  const creatorId = auth.userDetails.id
  const memberOptions = useMemo<GroupMemberOption[]>(
    () =>
      orgUsers
        // The creator is added automatically, and a deactivated member has
        // no business starting in a new group.
        .filter((u) => u.isActive && u.userId !== creatorId)
        .map((u) => {
          const user = userMap[u.userId]
          const fullName = user ? getUserFullName(userMap, u.userId) : ''
          return {
            userId: u.userId,
            name:
              fullName && fullName !== 'unknown'
                ? fullName
                : (user?.userEmail ?? u.userId),
            email: user?.userEmail,
          }
        })
        .sort((a, b) => a.name.localeCompare(b.name)),
    [orgUsers, userMap, creatorId],
  )

  const groupsQueryKey = useMemo(() => ['groups', orgId] as const, [orgId])

  const {
    data: groups,
    isLoading: isLoadingGroups,
    pagination: paginationConfig,
    refresh: refreshPagination,
  } = useOffsetPaginationQuery<Group>({
    preferenceKey: PREFERENCE_KEY,
    queryKey: groupsQueryKey,
    queryFn: ({ page, pageSize }) =>
      fetchGroupsPaginated(orgId, organisationClient, metadata, {
        page,
        pageSize,
      }),
  })

  /* ----------------------------- Group Mutations ---------------------------- */

  // The outcome of the last create attempt when the group exists but a later
  // step failed. Handing it back to the next attempt is what makes Retry
  // repeat only the failed steps instead of creating the group again.
  const [setupResult, setSetupResult] = useState<GroupSetupResult | null>(null)
  const [lastValues, setLastValues] = useState<CreateGroupFormValues | null>(
    null,
  )

  const createMutation = useMutation({
    mutationFn: (values: CreateGroupFormValues) =>
      runGroupSetup(
        { organisationClient, permissionsClient },
        metadata,
        { orgId, creatorId, ...values },
        setupResult ?? undefined,
      ),
    onSuccess: (result) => {
      // The group exists from here on, whatever happened after it.
      refreshPagination()
      if (result.grant?.status === 'done') {
        queryClient.invalidateQueries({
          queryKey: ['organisationGrants', orgId],
        })
      }
      if (groupSetupHasFailures(result)) {
        setSetupResult(result)
      } else {
        setCreateItemOpen(false)
        setSetupResult(null)
        setLastValues(null)
      }
    },
  })

  const closeCreateModal = () => {
    // Closing mid-request would drop the values Retry needs and let a partial
    // result land in a closed modal, so the modal stays until the attempt
    // settles. Cancel, Done and the form's buttons are disabled meanwhile.
    if (createMutation.isPending) return
    setCreateItemOpen(false)
    setSetupResult(null)
    setLastValues(null)
    createMutation.reset()
  }

  const deleteMutation = useMutation({
    mutationFn: async (groupId: string) => {
      return await organisationClient.deleteGroup({ groupId }, metadata)
    },
    onSuccess: () => {
      refreshPagination()
      setDeleteDialogOpen(false)
      setGroupToDelete(null)
    },
  })

  const updateMutation = useMutation({
    mutationFn: async (input: UpdateGroupInput) =>
      organisationClient.updateGroup(input, metadata),
    onSuccess: (updatedGroup) => {
      setGroupToEdit(null)
      queryClient.setQueryData(
        ['groups', orgId],
        (oldGroups: Group[] | undefined) =>
          oldGroups
            ? oldGroups.map((group) =>
                group.id === updatedGroup.id ? updatedGroup : group,
              )
            : [],
      )
    },
  })

  const handleCreateItem = async () => {
    setCreateItemOpen(true)
  }

  // Navigation for viewing a group
  const navigate = Route.useNavigate()
  const handleViewItem = (groupId: string) => {
    navigate({ to: `/orgs/${orgId}/groups/${groupId}` })
  }

  // Replace direct delete with confirmation dialog
  const handleDeleteGroup = (group: Group) => {
    setGroupToDelete(group)
    setDeleteDialogOpen(true)
  }

  const confirmDeleteGroup = () => {
    if (groupToDelete) {
      deleteMutation.mutate(groupToDelete.id)
    }
  }

  const cancelDeleteGroup = () => {
    setDeleteDialogOpen(false)
    setGroupToDelete(null)
  }

  return (
    <div>
      {/* Create Group Modal */}
      <Modal
        title="Create Group"
        description="A name is all a group needs. Members and permissions can be set now or later."
        icon={<PlusCircleIcon />}
        isOpen={createItemOpen}
        onClose={closeCreateModal}
        hideCloseButton={createMutation.isPending}
        maxWidth="sm:max-w-2xl"
      >
        <CreateGroupForm
          members={memberOptions}
          isLoadingMembers={isLoadingMembers}
          canAddMembers={canAddMembers}
          canGrant={canGrant}
          isPending={createMutation.isPending}
          error={
            createMutation.isError
              ? createGroupErrorMessage(createMutation.error)
              : undefined
          }
          result={setupResult ?? undefined}
          onSubmit={(values) => {
            setLastValues(values)
            createMutation.mutate(values)
          }}
          onRetry={() => {
            if (lastValues) createMutation.mutate(lastValues)
          }}
          onDone={closeCreateModal}
          onCancel={closeCreateModal}
        />
      </Modal>

      <EditNameModal
        isOpen={groupToEdit !== null}
        onClose={() => setGroupToEdit(null)}
        entityType="Group"
        initialName={groupToEdit?.name ?? ''}
        isPending={updateMutation.isPending}
        error={updateMutation.isError ? updateMutation.error : null}
        onSubmit={(newName) => {
          if (!groupToEdit) return
          updateMutation.mutate({ groupId: groupToEdit.id, name: newName })
        }}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        title="Delete Group"
        description={
          <>
            You&rsquo;re about to permanently delete{' '}
            <strong className="font-semibold text-text">
              {groupToDelete?.name}
            </strong>
            , including its members and grants. This can&rsquo;t be undone.
          </>
        }
        isOpen={deleteDialogOpen}
        onClose={cancelDeleteGroup}
        onConfirm={confirmDeleteGroup}
        confirmText="Delete"
        cancelText="Cancel"
        variant="destructive"
        isLoading={deleteMutation.isPending}
      />

      <div>
        {/* Groups Table */}
        <DataTable
          data={groups}
          columns={groupColumns}
          title="Organisation Groups"
          description="Groups of organisation users that can be used to manage permissions and notifications."
          isLoading={isLoadingGroups}
          onCreate={handleCreateItem}
          paginationConfig={paginationConfig}
          tableOptions={{
            meta: {
              userMap,
              actions: {
                onDelete: (group: Group) => handleDeleteGroup(group),
                onUpdate: (group: Group) => setGroupToEdit(group),
                onView: (group: Group) => handleViewItem(group.id),
              },
              hasError: updateMutation.isError,
            },
          }}
        />
      </div>
    </div>
  )
}
